import { updateState } from '@angular-architects/ngrx-toolkit';
import { computed, DestroyRef, inject, Signal } from '@angular/core';
import {
    IConversationEvent,
    IConversationInfoEvent,
    IFileEntry,
    IMessage,
    IParticipantTypingEvent,
} from '@dxp/core/models';
import {
    signalStore,
    withFeature,
    withHooks,
    withMethods,
    withProps,
    withState,
} from '@ngrx/signals';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
    catchError,
    concat,
    concatMap,
    distinct,
    filter,
    finalize,
    from,
    map,
    merge,
    mergeMap,
    Observable,
    of,
    switchMap,
    take,
    tap,
    timer,
} from 'rxjs';
import { CHAT_SIGNALR_HUB } from '@dxp/core/signalr';
import { TranslationService, UploadService } from '@dxp/core/services';
import { environment } from '@dxp/core/environment';
import { withWorkItems } from '../../workitems/workitems.feature';
import { WORKITEMS_STORE } from '../../workitems/workitems.token';
import { IChatConversationStore } from './chat.models';
import {
    areMessagesEquivalent,
    dedupeById,
    hasConversationInfoChanged,
    sortByCreatedAsc,
} from './chat.helpers';

const DELAY_CONNECT_TIME = 500;
const TYPING_VISIBLE_LENGTH = 2000;

interface IChatStoreState {
    conversations: Record<string, IChatConversationStore>;
    composed: Record<string, string>;
    attachments: Record<string, IFileEntry[]>;
}

const initialState: IChatStoreState = {
    conversations: {},
    composed: {},
    attachments: {},
};

export const ChatStore = signalStore(
    { providedIn: 'root' },
    environment.storeWithDevTools('ChatStore'),
    withFeature(() => withWorkItems(inject(WORKITEMS_STORE))),
    withState<IChatStoreState>(initialState),
    withProps(store => {
        const conversations$ = toObservable(store.conversations);

        const waitForConversation$ = (
            conversationId: string,
        ): Observable<IChatConversationStore> => {
            return conversations$.pipe(
                map(records => records[conversationId]),
                filter(
                    (conversation): conversation is IChatConversationStore =>
                        conversation !== undefined,
                ),
                take(1),
            );
        };

        const waitForHistory$ = (
            conversationId: string,
        ): Observable<IChatConversationStore> => {
            return conversations$.pipe(
                map(records => records[conversationId]),
                filter(
                    (conversation): conversation is IChatConversationStore =>
                        conversation !== undefined &&
                        conversation.messages !== undefined &&
                        conversation.messages.length > 0,
                ),
                take(1),
            );
        };

        const conversationById = computed(() => store.conversations());

        const conversationSelectorCache = new Map<
            string,
            Signal<IChatConversationStore | undefined>
        >();

        const selectConversation = (conversationId: string) => {
            const existing = conversationSelectorCache.get(conversationId);
            if (existing) return existing;

            const sel = computed(() => conversationById()[conversationId]);
            conversationSelectorCache.set(conversationId, sel);
            return sel;
        };

        const composedById = computed(() => store.composed());

        const composedSelectorCache = new Map<
            string,
            Signal<string | undefined>
        >();

        const selectComposed = (conversationId: string) => {
            const existing = composedSelectorCache.get(conversationId);
            if (existing) return existing;

            const sel = computed(() => composedById()[conversationId]);
            composedSelectorCache.set(conversationId, sel);
            return sel;
        };

        const attachmentsById = computed(() => store.attachments());

        const attachmentsSelectorCache = new Map<
            string,
            Signal<IFileEntry[] | undefined>
        >();

        const selectAttachments = (conversationId: string) => {
            const existing = attachmentsSelectorCache.get(conversationId);
            if (existing) return existing;

            const sel = computed(() => attachmentsById()[conversationId]);
            attachmentsSelectorCache.set(conversationId, sel);
            return sel;
        };

        const purgeSelectors = (conversationId: string) => {
            conversationSelectorCache.delete(conversationId);
            composedSelectorCache.delete(conversationId);
            attachmentsSelectorCache.delete(conversationId);
        };

        return {
            selectConversation: selectConversation,
            selectComposed: selectComposed,
            selectAttachments: selectAttachments,
            purgeSelectors: purgeSelectors,
            waitForConversation$: waitForConversation$,
            waitForHistory$: waitForHistory$,
        };
    }),
    withMethods(store => ({
        getConversation: store.selectConversation,
        getComposed: store.selectComposed,
        getAttachments: store.selectAttachments,
        updateComposed(conversationId: string, composed: string) {
            updateState(store, 'UPDATE_CHAT_COMPOSED', state => ({
                ...state,
                composed: {
                    ...state.composed,
                    [conversationId]: composed,
                },
            }));
        },
        updateAttachments(conversationId: string, attachments: IFileEntry[]) {
            updateState(store, 'UPDATE_CHAT_ATTACHMENTS', state => ({
                ...state,
                attachments: {
                    ...state.attachments,
                    [conversationId]: attachments,
                },
            }));
        },
        updateConversationInfo(conversationInfo: IConversationInfoEvent) {
            const id = conversationInfo.conversationId;

            const workItemCustomerName = store
                .workItems()
                .find(wi => wi.primaryConversationId === id)?.displayCustomer;

            updateState(store, 'UPSERT_CHAT_CONVERSATION_INFO', state => {
                const existing = state.conversations[id];

                const nextInfo: IConversationInfoEvent = {
                    ...conversationInfo,
                    customerName:
                        workItemCustomerName ?? conversationInfo.customerName,
                };

                if (!existing) {
                    return {
                        ...state,
                        conversations: {
                            ...state.conversations,
                            [id]: {
                                information: nextInfo,
                                messages: [],
                                typing: false,
                            },
                        },
                    };
                }

                if (!existing.information) {
                    return {
                        ...state,
                        conversations: {
                            ...state.conversations,
                            [id]: {
                                ...existing,
                                information: nextInfo,
                            },
                        },
                    };
                }

                if (
                    !hasConversationInfoChanged(existing.information, nextInfo)
                ) {
                    return state;
                }

                return {
                    ...state,
                    conversations: {
                        ...state.conversations,
                        [id]: {
                            ...existing,
                            information: nextInfo,
                        },
                    },
                };
            });
        },
        updateCustomerName(conversationId: string, customerName: string) {
            updateState(store, 'UPDATE_CHAT_CUSTOMER_NAME', state => {
                const existing = state.conversations[conversationId];
                if (!existing?.information) return state;

                if (existing.information.customerName === customerName)
                    return state;

                return {
                    ...state,
                    conversations: {
                        ...state.conversations,
                        [conversationId]: {
                            ...existing,
                            information: {
                                ...existing.information,
                                customerName,
                            },
                        },
                    },
                };
            });
        },
        closeConversation(conversation: IConversationEvent) {
            const conversationId = conversation.conversationId;

            const hasAny =
                store.conversations()[conversationId] !== undefined ||
                store.composed()[conversationId] !== undefined ||
                store.attachments()[conversationId] !== undefined;

            if (!hasAny) return;

            store.purgeSelectors(conversationId);

            updateState(store, 'REMOVE_CHAT_CONVERSATION', state => {
                const { [conversationId]: _, ...conversations } =
                    state.conversations;
                const { [conversationId]: __, ...composed } = state.composed;
                const { [conversationId]: ___, ...attachments } =
                    state.attachments;

                return { ...state, conversations, composed, attachments };
            });
        },
        updateParticipantTyping(conversationId: string, typing: boolean) {
            updateState(store, 'UPDATE_CHAT_PARTICIPANT_TYPING', state => {
                const existing = state.conversations[conversationId];
                if (!existing) return state;

                if (existing.typing === typing) return state;

                return {
                    ...state,
                    conversations: {
                        ...state.conversations,
                        [conversationId]: {
                            ...existing,
                            typing,
                        },
                    },
                };
            });
        },
        updateChatMessages(conversationId: string, messages: IMessage[]) {
            updateState(store, 'UPDATE_CHAT_MESSAGES', state => {
                const existing = state.conversations[conversationId];
                if (!existing) return state;

                if (
                    existing.typing === false &&
                    areMessagesEquivalent(existing.messages, messages)
                ) {
                    return state;
                }

                return {
                    ...state,
                    conversations: {
                        ...state.conversations,
                        [conversationId]: {
                            ...existing,
                            messages,
                            typing: false,
                        },
                    },
                };
            });
        },
    })),
    withHooks({
        onInit(
            store,
            chatHub = inject(CHAT_SIGNALR_HUB),
            translationService = inject(TranslationService),
            uploadService = inject(UploadService),
            destroyRef = inject(DestroyRef),
        ) {
            store.onWorkItemAdded$
                .pipe(
                    filter(
                        workitem =>
                            (workitem.workItemState === 'Preview' ||
                                workitem.workItemState === 'Connected') &&
                            (workitem.primaryMediaType === 'Webchat' ||
                                workitem.primaryMediaType === 'Messaging'),
                    ),
                    mergeMap(workitem => {
                        translationService.addConversation(
                            workitem.primaryConversationId,
                            workitem.channelId,
                        );

                        return timer(DELAY_CONNECT_TIME).pipe(
                            tap(() => {
                                chatHub.peekOrConnect({
                                    conversationId:
                                        workitem.primaryConversationId,
                                });
                            }),
                        );
                    }),
                    takeUntilDestroyed(destroyRef),
                )
                .subscribe();

            chatHub.conversationInfo$
                .pipe(
                    tap(conversationInfo => {
                        store.updateConversationInfo(conversationInfo);
                    }),
                    takeUntilDestroyed(destroyRef),
                )
                .subscribe();

            const ended$ = merge(
                chatHub.chatEnded$.pipe(
                    map(
                        (e): IConversationEvent => ({
                            conversationId: e.conversationId,
                        }),
                    ),
                ),
                store.onWorkItemRemoved$.pipe(
                    map(
                        wi =>
                            ({
                                conversationId: wi.primaryConversationId,
                            }) as IConversationEvent,
                    ),
                ),
            );

            ended$
                .pipe(
                    distinct(e => e.conversationId),
                    concatMap((conversation: IConversationEvent) => {
                        translationService.removeConversation(
                            conversation.conversationId,
                        );

                        const attachments =
                            store.attachments()[conversation.conversationId] ??
                            [];

                        const toRemove = attachments.filter(
                            r => r.state === 'uploaded' && r.response?.success,
                        );

                        const remove$ =
                            toRemove.length > 0
                                ? from(toRemove).pipe(
                                      concatMap(file => {
                                          const path =
                                              file.response?.data.files[0] ??
                                              '';
                                          const { directory, filename } =
                                              uploadService.splitDirAndFile(
                                                  path,
                                              );

                                          return uploadService
                                              .removeJoditFile(
                                                  directory,
                                                  filename,
                                              )
                                              .pipe(
                                                  take(1),
                                                  catchError(() => of(void 0)),
                                              );
                                      }),
                                  )
                                : of(void 0);

                        return remove$.pipe(
                            finalize(() => {
                                store.closeConversation(conversation);
                            }),
                        );
                    }),
                    takeUntilDestroyed(destroyRef),
                )
                .subscribe();

            chatHub.participantTyping$
                .pipe(
                    filter(
                        (evt: IParticipantTypingEvent) =>
                            evt.sender.role === 'Customer',
                    ),
                    switchMap(evt =>
                        concat(
                            of({ id: evt.conversationId, typing: true }),
                            timer(TYPING_VISIBLE_LENGTH).pipe(
                                map(() => ({
                                    id: evt.conversationId,
                                    typing: false,
                                })),
                            ),
                        ),
                    ),
                    tap(({ id, typing }) => {
                        store.updateParticipantTyping(id, typing);
                    }),
                    takeUntilDestroyed(destroyRef),
                )
                .subscribe();

            chatHub.chatHistory$
                .pipe(
                    switchMap(chatHistory =>
                        store
                            .waitForConversation$(chatHistory.conversationId)
                            .pipe(map(_ => chatHistory)),
                    ),
                    switchMap(chatHistory => {
                        const sorted = sortByCreatedAsc(
                            chatHistory.messages ?? [],
                        );

                        return translationService
                            .translateFromForeign$(
                                sorted,
                                chatHistory.conversationId,
                            )
                            .pipe(
                                map(translatedMessages => ({
                                    chatHistory,
                                    messages: translatedMessages,
                                })),
                            );
                    }),
                    tap(({ chatHistory, messages }) => {
                        store.updateChatMessages(
                            chatHistory.conversationId,
                            messages,
                        );
                    }),
                    takeUntilDestroyed(destroyRef),
                )
                .subscribe();

            chatHub.chatMessage$
                .pipe(
                    switchMap(chatMessage =>
                        store
                            .waitForHistory$(chatMessage.conversationId)
                            .pipe(map(existing => ({ chatMessage, existing }))),
                    ),
                    switchMap(({ chatMessage, existing }) => {
                        const messages = existing?.messages ?? [];

                        return translationService
                            .translateFromForeign$(
                                [chatMessage.message],
                                chatMessage.conversationId,
                            )
                            .pipe(
                                map(translatedMsg => {
                                    const combined = dedupeById([
                                        ...messages,
                                        ...translatedMsg,
                                    ]);

                                    const sorted = sortByCreatedAsc(combined);

                                    return {
                                        chatMessage,
                                        messages: sorted,
                                    };
                                }),
                            );
                    }),
                    tap(({ chatMessage, messages }) => {
                        store.updateChatMessages(
                            chatMessage.conversationId,
                            messages,
                        );
                    }),
                    takeUntilDestroyed(destroyRef),
                )
                .subscribe();
        },
    }),
);
